import { Box, CircularProgress, Table, TableBody, TableCell, TableFooter, TableHead, TablePagination, TableRow } from "@mui/material";
import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { faFileInvoice } from '@fortawesome/free-solid-svg-icons';
import TablePaginationActions from '../../../base-components/TablePaginationActions';
import CustomModal from '../../../base-components/CustomModal';
import dayjs from 'dayjs';

const BASE_URL = process.env.REACT_APP_BASE_URL;
function POSOrders({ company_id, location_id, role }) {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [orderToView, setOrderToView] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [status, setStatus] = useState('');
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);

  // Fetch bank accounts when component mounts
  useEffect(() => {
    if (company_id) {
      getBankAccounts();
    }
  }, [company_id]);

  useEffect(() => {
    if (company_id && location_id && !isSearching) {
      getOrders();
    }
  }, [company_id, location_id, startDate, endDate, status, isSearching]);

  const getBankAccounts = async () => {
    try {
      const response = await fetch(`${BASE_URL}company-bank-accounts/company/${company_id}`);
      const data = await response.json();
      if (response.ok) {
        setBankAccounts(data.bankAccounts || []);
        console.log('Bank accounts fetched:', data.bankAccounts);
      } else {
        console.log('No bank accounts found or error:', data.message);
        setBankAccounts([]);
      }
    } catch (error) {
      console.error('Error fetching bank accounts:', error);
      setBankAccounts([]);
    }
  };

  const getOrders = async () => {
    setLoading(true);
    try {
      let url = `${BASE_URL}pos?company_id=${company_id}&location_id=${location_id}`;
      if (startDate) {
        url += `&startDate=${startDate}`;
      }
      if (endDate) {
        url += `&endDate=${endDate}`;
      }
      if (status) {
        url += `&status=${status}`;
      }
      const response = await fetch(url);
      const data = await response.json();
      if (response.ok) {
        // Handle both array response and object with orders property
        if (Array.isArray(data)) {
          setOrders(data);
        } else {
          setOrders(data.orders || []);
        }
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleView = (order) => {
    setOrderToView(order);
    setIsViewModalOpen(true);
  };

  const handleInvoiceSearch = async () => {
    if (!invoiceSearch.trim()) {
      setIsSearching(false);
      getOrders();
      return;
    }

    setIsSearching(true);
    setLoading(true);
    try {
      let url = `${BASE_URL}pos/search/invoice/${encodeURIComponent(invoiceSearch.trim())}?company_id=${company_id}&location_id=${location_id}`;
      const response = await fetch(url);
      const data = await response.json();
      if (response.ok) {
        // Handle response format: { orders: [...] } or direct array
        if (data.orders && Array.isArray(data.orders)) {
          setOrders(data.orders);
        } else if (Array.isArray(data)) {
          setOrders(data);
        } else {
          setOrders([]);
        }
      } else {
        // No orders found
        setOrders([]);
      }
    } catch (error) {
      console.error('Error searching by invoice number:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClearSearch = () => {
    setInvoiceSearch('');
    setIsSearching(false);
    getOrders();
  };

  const paginatedOrders =
    !orders || orders.length === 0
      ? []
      : rowsPerPage === -1
        ? orders
        : orders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <> 
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-bold text-xl text-gx-ink-soft">POS Orders</h2>
        <div className="flex gap-4 items-center flex-wrap">
          {/* Search Section */}
          <div className="flex gap-2 items-center">
            {/* Invoice Number Search */}
            <input
              type="text"
              placeholder="Search by Invoice No..."
              value={invoiceSearch}
              onChange={(e) => setInvoiceSearch(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleInvoiceSearch();
                }
              }}
              className="border border-gx-border bg-gx-canvas p-2 rounded-xl outline-0 text-gx-ink-soft w-64"
            />
            <button
              onClick={handleInvoiceSearch}
              className="gx-cta whitespace-nowrap"
            >
              Search
            </button>
            {isSearching && (
              <button
                onClick={handleClearSearch}
                className="gx-cta-secondary whitespace-nowrap"
              >
                Clear
              </button>
            )}
          </div>
          <input
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setIsSearching(false);
              setInvoiceSearch('');
            }}
            className="border border-gx-border bg-gx-canvas p-2 rounded-xl outline-0 text-gx-ink-soft"
          />
          <input
            type="date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setIsSearching(false);
              setInvoiceSearch('');
            }}
            className="border border-gx-border bg-gx-canvas p-2 rounded-xl outline-0 text-gx-ink-soft"
          />
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setIsSearching(false);
              setInvoiceSearch('');
            }}
            className="border border-gx-border bg-gx-canvas p-2 rounded-xl outline-0 text-gx-ink-soft"
          >
            <option value="">All Status</option>
            <option value="Completed">Completed</option>
            <option value="Cancelled">Cancelled</option>
            <option value="Refunded">Refunded</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center mt-20">
          <CircularProgress
            thickness={4}
            size={35}
            sx={{
              'svg circle': {
                stroke: 'url(#gradientColors)',
              },
            }}
          />
        </div>
      ) : orders && orders.length !== 0 ? (
        <Box className="bg-gx-surface rounded-gx border border-gx-border shadow-gx m-5 animate-fadeIn overflow-y-auto overflow-x-auto">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order ID</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Items</TableCell>
                <TableCell>Subtotal</TableCell>
                <TableCell>Discount</TableCell>
                <TableCell>Total Amount</TableCell>
                <TableCell>Payment Method</TableCell>
                <TableCell>Member</TableCell>
                <TableCell>Sold By</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedOrders.map((order, index) => (
                <TableRow hover key={index}>
                  <TableCell className="font-semibold">{order.order_id}</TableCell>
                  <TableCell>{dayjs(order.created_at).format('MMM DD, YYYY HH:mm')}</TableCell>
                  <TableCell>{order.items?.length || 0} items</TableCell>
                  <TableCell>Rs. {order.subtotal?.toFixed(2) || 0}</TableCell>
                  <TableCell>Rs. {order.discount?.toFixed(2) || 0}</TableCell>
                  <TableCell className="font-semibold">Rs. {order.total_amount?.toFixed(2) || 0}</TableCell>
                  <TableCell>{order.payment_method}</TableCell>
                  <TableCell>
                    {order.member_id?.name || order.customer_name || 'Walk-in'}
                  </TableCell>
                  <TableCell>{order.sold_by?.name || 'N/A'}</TableCell>
                  <TableCell>
                    <div
                      className={`font-bold p-2 px-4 rounded-full text-center ${order.status === 'Completed'
                          ? 'bg-green-700 text-white'
                          : order.status === 'Cancelled'
                            ? 'bg-red-700 text-white'
                            : 'bg-yellow-700 text-white'
                        }`}
                    >
                      {order.status}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div
                      className="text-gx-ink font-bold hover:cursor-pointer hover:text-green-700"
                      onClick={() => handleView(order)}
                    >
                      <FontAwesomeIcon icon={faEye} /> View
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                  colSpan={11}
                  count={orders.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  slotProps={{
                    select: {
                      inputProps: {
                        'aria-label': 'rows per page',
                      },
                      native: true,
                    },
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </Box>
      ) : (
        <div className="bg-gx-border w-full p-3 mt-10 rounded-lg m-10">
          <h3 className="text-xl font-bold text-center text-gx-ink-soft">No Orders Found</h3>
        </div>
      )}

      {/* View Order Modal */}
      <CustomModal isModal={isViewModalOpen} handleModal={() => setIsViewModalOpen(false)} size="2xl">
        {orderToView && (
          <>
            <h1 className="font-bold text-3xl text-gx-ink-soft p-5 border-b mx-2">Order Details</h1>
            <div className="m-5 text-gx-ink-soft">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="font-semibold">Order ID:</label>
                  <p className="font-bold text-lg">{orderToView.order_id}</p>
                </div>
                <div>
                  <label className="font-semibold">Date:</label>
                  <p>{dayjs(orderToView.created_at).format('MMM DD, YYYY HH:mm')}</p>
                </div>
                <div>
                  <label className="font-semibold">Payment Method:</label>
                  <p>{orderToView.payment_method}</p>
                </div>
                {orderToView.transaction_id && (
                  <div>
                    <label className="font-semibold">Transaction ID:</label>
                    <p>{orderToView.transaction_id}</p>
                  </div>
                )}
                <div>
                  <label className="font-semibold">Customer:</label>
                  {orderToView.member_id ? (
                    <p>{orderToView.member_id.name} (Member)</p>
                  ) : orderToView.customer_name ? (
                    <div>
                      <p className="font-semibold">{orderToView.customer_name}</p>
                      {orderToView.customer_phone && <p className="text-sm text-gx-ink-soft">Phone: {orderToView.customer_phone}</p>}
                      {orderToView.customer_email && <p className="text-sm text-gx-ink-soft">Email: {orderToView.customer_email}</p>}
                      {orderToView.customer_cnic && <p className="text-sm text-gx-ink-soft">CNIC: {orderToView.customer_cnic}</p>}
                    </div>
                  ) : (
                    <p>Walk-in Customer</p>
                  )}
                </div>
                <div>
                  <label className="font-semibold">Sold By:</label>
                  <p>{orderToView.sold_by?.name || 'N/A'}</p>
                </div>
                <div>
                  <label className="font-semibold">Status:</label>
                  <p>
                    <span
                      className={`font-bold p-2 px-4 rounded-full ${orderToView.status === 'Completed'
                          ? 'bg-green-700 text-white'
                          : orderToView.status === 'Cancelled'
                            ? 'bg-red-700 text-white'
                            : 'bg-yellow-700 text-white'
                        }`}
                    >
                      {orderToView.status}
                    </span>
                  </p>
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <h3 className="font-bold text-lg mb-3">Order Items</h3>
                <div className="space-y-2">
                  {orderToView.items?.map((item, index) => (
                    <div key={index} className="flex justify-between items-center p-2 bg-gx-canvas rounded">
                      <div>
                        <p className="font-semibold">{item.product_name || 'N/A'}</p>
                        <p className="text-sm text-gx-ink-soft">
                          Qty: {item.quantity} × Rs. {item.unit_price || 0}
                          {item.discount > 0 && ` - Discount: Rs. ${item.discount}`}
                        </p>
                      </div>
                      <p className="font-semibold">
                        Rs. {(item.subtotal || (item.quantity * (item.unit_price || 0) - (item.discount || 0))).toFixed(2)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <div className="flex justify-between mb-2">
                  <span>Subtotal:</span>
                  <span>Rs. {orderToView.subtotal?.toFixed(2) || 0}</span>
                </div>
                {orderToView.discount > 0 && (
                  <div className="flex justify-between mb-2 text-red-600">
                    <span>Discount:</span>
                    <span>-Rs. {orderToView.discount?.toFixed(2) || 0}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>Total:</span>
                  <span className="text-gx-navy">Rs. {orderToView.total_amount?.toFixed(2) || 0}</span>
                </div>
              </div>

              <div className="flex justify-end mt-4 pt-4 border-t">
                <button
                  onClick={() => {
                    navigate(`/pos-invoice/${orderToView._id}`);
                    setIsViewModalOpen(false);
                  }}
                  className="gx-cta gap-2"
                >
                  <FontAwesomeIcon icon={faFileInvoice} />
                  View Invoice
                </button>
              </div>
            </div>
          </>
        )}
      </CustomModal>
    </>
  );
}

export default POSOrders;

